import { MapPinned, Shield, Swords, Flag, AlertTriangle } from "lucide-react";
import { storyElement } from "@/lib/story-element";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";

interface TerritoryPanelProps {
  controlledLocations: any[] | null;
  contestedLocations: any[] | null;
  rivalFactions: any[] | null;
  territoryScale: string | null;
}

export function TerritoryStatusWidget({ controlledCount, contestedCount, compact }: { controlledCount: number | null; contestedCount: number | null; compact?: boolean }) {
  const ctrl = controlledCount ?? 0;
  const cont = contestedCount ?? 0;

  if (compact) {
    return (
      <div className="flex items-center gap-1.5 text-muted-foreground">
        <MapPinned className="w-3 h-3" />
        <span className="text-[10px]">{ctrl} held</span>
        {cont > 0 && <span className="text-[10px] text-destructive">⚔️{cont}</span>}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <MapPinned className="w-4 h-4 text-muted-foreground" />
      <span className="text-xs font-medium text-foreground">{ctrl} territories</span>
      {cont > 0 && <Badge variant="destructive" className="text-[10px]">{cont} contested</Badge>}
    </div>
  );
}

export function TerritoryPanel({ controlledLocations, contestedLocations, rivalFactions, territoryScale }: TerritoryPanelProps) {
  const controlled = controlledLocations || [];
  const contested = contestedLocations || [];
  const rivals = rivalFactions || [];
  const scale = territoryScale || "districts";

  const hasData = controlled.length > 0 || contested.length > 0 || rivals.length > 0;

  if (!hasData) {
    return (
      <div className="text-xs text-muted-foreground/50 text-center py-4">
        No territory data yet. Claim locations to build your domain.
      </div>
    );
  }

  return (
    <div {...storyElement("territory-panel")} className="space-y-4">
      {/* Scale Badge */}
      <Badge variant="outline" className="text-[10px] capitalize">{scale} scale</Badge>

      {/* Controlled */}
      {controlled.length > 0 && (
        <div className="space-y-2">
          <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest flex items-center gap-1.5">
            <Flag className="w-3 h-3" /> Controlled ({controlled.length})
          </div>
          {controlled.map((loc: any, i: number) => (
            <div key={loc.id || i} className="flex items-center justify-between text-xs border border-border rounded-md px-2 py-1.5">
              <span className="font-medium text-foreground truncate">{loc.name || loc.id}</span>
              <div className="flex items-center gap-1.5">
                {loc.production_type && (
                  <Badge variant="secondary" className="text-[10px]">{loc.production_type}</Badge>
                )}
                {loc.defense_level && (
                  <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground">
                    <Shield className="w-2.5 h-2.5" />{loc.defense_level}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Contested */}
      {contested.length > 0 && (
        <>
          <Separator />
          <div className="space-y-2">
            <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest flex items-center gap-1.5">
              <Swords className="w-3 h-3 text-destructive" /> Contested ({contested.length})
            </div>
            {contested.map((loc: any, i: number) => (
              <div key={loc.id || i} className="flex items-center justify-between text-xs border border-destructive/30 rounded-md px-2 py-1.5">
                <span className="font-medium text-foreground truncate">{loc.name || loc.id}</span>
                <div className="flex flex-wrap gap-1">
                  {(loc.claimants || []).map((c: any, ci: number) => (
                    <Badge key={ci} variant="outline" className="text-[10px]">{c.faction}: {c.strength}</Badge>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Rival Factions */}
      {rivals.length > 0 && (
        <>
          <Separator />
          <div className="space-y-2">
            <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest flex items-center gap-1.5">
              <AlertTriangle className="w-3 h-3" /> Rival Factions ({rivals.length})
            </div>
            {rivals.map((faction: any, i: number) => (
              <div key={i} className="flex items-center justify-between text-xs">
                <span className="text-foreground">{faction.name || `Faction ${i + 1}`}</span>
                <div className="flex items-center gap-1.5">
                  {faction.territory_count != null && (
                    <span className="text-[10px] text-muted-foreground">{faction.territory_count} territories</span>
                  )}
                  {faction.threat_level && (
                    <Badge variant={faction.threat_level === "high" ? "destructive" : "secondary"} className="text-[10px]">
                      {faction.threat_level}
                    </Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
